export interface GarageObject {
  key: string
  size: number
  lastModified: string
  contentType: string
  url: string
}

export interface APIKeyRecord {
  id: string
  name: string
  keyPrefix: string
  level: 'admin' | 'standard'
  createdAt: string
  revokedAt?: string | null
}

export interface CreateAPIKeyResult {
  plaintext: string
  record: APIKeyRecord
}

export interface UserSessionBrief {
  id: string
  character: string
  storyPath: string
  lastActive: string
}

export interface AuditMsg {
  role: string
  content: string
  type?: string
  position: number
}

export interface AuditRequest {
  id: string
  llmType: string
  model: string
  response: string
  metadata?: Record<string, unknown>
  createdAt: string
  messages: AuditMsg[]
}

export interface AuditSessionSummary {
  id: string
  createdAt: string
  requestCount: number
  messageCount: number
}

export interface AuditSessionDetail {
  id: string
  createdAt: string
  requests: AuditRequest[]
}

export interface MessageFrequency {
  content: string
  role: string
  count: number
}

export interface AuditStats {
  sessionCount: number
  requestCount: number
  messageCount: number
  topUserMessages: MessageFrequency[]
  topAssistantMessages: MessageFrequency[]
}

export interface LLMMessage {
  role: string
  content: string
  type?: string
}

export interface SessionSummary {
  id: string
  character: string
  storyPath: string
  connections: number
  messageCount: number
  lastActive: string
  features: string[]
  storyPhase?: string
  turnCount?: number
}

export interface SessionDetail extends SessionSummary {
  history: LLMMessage[]
  conversationSummary?: string
  completedObjectives?: Record<string, boolean>
  playerState?: Record<string, string>
  lastStorytellerReasoning?: string
}

export interface PrefabEntry {
  phrases: string
  not_for?: string
  alternate?: string
}

export interface CachedResponseEntry {
  phrases: string
  response: string
  not_for?: string
  animations?: string
  has_audio?: boolean
}

export interface AdminConfig {
  prefabs?: Record<string, PrefabEntry>
}

export interface StoryFacts {
  public: string[]
  private: string[]
}

export interface StoryObjective {
  id: string
  description: string
  completionType: string
  condition?: string
  choice?: string
  urgency: string
  tactics: string[]
}

export interface StoryTrigger {
  id: string
  condition: string
  goto: string
  priority: number
}

export interface StoryPhase {
  description: string
  tone: string
  maxTurns: number
  objectives: StoryObjective[]
  facts: StoryFacts
  triggers: StoryTrigger[]
  onComplete: string
  onFailure: string
}

export interface StoryDetail {
  id: string
  title: string
  version: string
  description: string
  startPhase: string
  character: { name: string; personality: string }
  globalFacts: StoryFacts
  phases: Record<string, StoryPhase>
}

export interface StoryListItem {
  id: string
  title: string
  version: string
  description: string
  characterName: string
  characterVersion: number
  sourceFilename: string
  inDatabase: boolean
  onDisk: boolean
}

export interface CharacterListItem {
  id: string
  name: string
  version: number
  storyCount: number
  createdAt: string
  updatedAt: string
}

export interface CharacterDetail {
  id: string
  name: string
  version: number
  personality: string
  stories: { id: string; title: string; sourceFilename: string }[]
  createdAt: string
  updatedAt: string
}

export interface DbPrefabDetail {
  id: string
  name: string
  phrases: string
  alternate: string
  notFor: string
  stories: { id: string; title: string; sourceFilename: string }[]
  createdAt: string
  updatedAt: string
}

export interface GestureSynonym {
  stemWords: string
  gestureName: string
}

export interface GestureAnimation {
  id: string
  durationMs: number
  bodyWeight: number
  isAdditive: boolean
  bodyGlbUrl: string
  faceJsonUrl: string
  createdAt: string
}

export interface GestureDTO {
  id: string
  name: string
  position: number
  animation: GestureAnimation | null
}

export interface CharacterAnimConfig {
  gestures: GestureDTO[]
  vocalCues: string[]
  gestureSynonyms: GestureSynonym[]
  a2fConfig: Record<string, unknown>
}

export interface DbCachedResponseDetail {
  id: string
  name: string
  phrases: string
  notFor: string
  response: string
  animations: string
  stories: { id: string; title: string; sourceFilename: string; characterName: string; characterVersion: number; hasAudio: boolean }[]
  createdAt: string
  updatedAt: string
}

export function createAdminApi(getApiKey: () => string) {
  function headers() {
    return { 'X-API-Key': getApiKey(), 'Content-Type': 'application/json' }
  }

  async function request<T>(url: string, method: string, body?: unknown): Promise<T> {
    const res = await fetch(url, {
      method,
      headers: headers(),
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
    return res.json()
  }

  return {
    getConfig: () => request<AdminConfig>('/admin/config', 'GET'),
    getPrefabs: () => request<Record<string, PrefabEntry>>('/admin/prefabs', 'GET'),
    setPrefabs: (data: Record<string, PrefabEntry>) => request('/admin/prefabs', 'PUT', data),
    getCachedResponses: () => request<Record<string, CachedResponseEntry>>('/admin/cached-responses', 'GET'),
    setCachedResponses: (data: Record<string, CachedResponseEntry>) => request('/admin/cached-responses', 'PUT', data),
    regenCachedAudio: () => request('/admin/cached-responses/regenerate', 'POST'),
    getFeatures: () => request<Record<string, boolean>>('/admin/features', 'GET'),
    setFeatures: (data: Record<string, boolean>) => request('/admin/features', 'PUT', data),
    getSessions: () => request<SessionSummary[]>('/admin/sessions', 'GET'),
    getSession: (id: string) => request<SessionDetail>(`/admin/sessions/${id}`, 'GET'),
    getAuditSessions: () => request<AuditSessionSummary[]>('/admin/audit/sessions', 'GET'),
    getAuditSession: (id: string) => request<AuditSessionDetail>(`/admin/audit/sessions/${id}`, 'GET'),
    getAuditStats: () => request<AuditStats>('/admin/audit/stats', 'GET'),
    getStories: () => request<StoryListItem[]>('/admin/stories', 'GET'),
    ingestStory: (filename: string) => request<StoryListItem>(`/admin/stories/${filename}/ingest`, 'POST'),
    deleteStory: (id: string) => request<{ deleted: string }>(`/admin/stories/${id}/delete`, 'DELETE'),
    exportStory: async (filename: string) => {
      const res = await fetch(`/api/stories/${encodeURIComponent(filename)}/export`, {
        headers: { 'X-API-Key': getApiKey() },
      })
      if (!res.ok) {
        let msg = `${res.status} ${res.statusText}`
        try { const j = JSON.parse(await res.text()); if (j?.error) msg = j.error } catch { /* keep status text */ }
        throw new Error(msg)
      }
      const text = await res.text()
      const blob = new Blob([text], { type: 'application/yaml' })
      const url = URL.createObjectURL(blob)
      const cd = res.headers.get('Content-Disposition') ?? ''
      const match = cd.match(/filename="([^"]+)"/)
      const a = document.createElement('a')
      a.href = url
      a.download = match ? match[1] : `${filename}.yaml`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },
    getCharacters: () => request<CharacterListItem[]>('/admin/characters', 'GET'),
    getCharacter: (id: string) => request<CharacterDetail>(`/admin/characters/${id}`, 'GET'),
    updateCharacterPersonality: (id: string, personality: string) =>
      request<CharacterDetail>(`/admin/characters/${id}`, 'PUT', { personality }),
    getStory: (filename: string) => request<StoryDetail>(`/admin/stories/${encodeURIComponent(filename)}/detail`, 'GET'),
    listDbPrefabs: () => request<DbPrefabDetail[]>('/admin/prefabs/', 'GET'),
    getDbPrefab: (id: string) => request<DbPrefabDetail>(`/admin/prefabs/${id}`, 'GET'),
    createDbPrefab: (name: string, phrases: string, alternate: string, notFor: string) =>
      request<DbPrefabDetail>('/admin/prefabs/', 'POST', { name, phrases, alternate, notFor }),
    updateDbPrefab: (id: string, phrases: string, alternate: string, notFor: string) =>
      request<DbPrefabDetail>(`/admin/prefabs/${id}`, 'PUT', { phrases, alternate, notFor }),
    deleteDbPrefab: async (id: string) => {
      const res = await fetch(`/admin/prefabs/${id}`, { method: 'DELETE', headers: headers() })
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
    },
    setDbPrefabStories: (id: string, storyIds: string[]) =>
      request<DbPrefabDetail>(`/admin/prefabs/${id}/stories`, 'PUT', { storyIds }),
    listDbCachedResponses: () => request<DbCachedResponseDetail[]>('/admin/cached-responses/', 'GET'),
    getDbCachedResponse: (id: string) => request<DbCachedResponseDetail>(`/admin/cached-responses/${id}`, 'GET'),
    createDbCachedResponse: (name: string, phrases: string, response: string, notFor: string, animations: string) =>
      request<DbCachedResponseDetail>('/admin/cached-responses/', 'POST', { name, phrases, response, notFor, animations }),
    updateDbCachedResponse: (id: string, phrases: string, response: string, notFor: string, animations: string) =>
      request<DbCachedResponseDetail>(`/admin/cached-responses/${id}`, 'PUT', { phrases, response, notFor, animations }),
    deleteDbCachedResponse: async (id: string) => {
      const res = await fetch(`/admin/cached-responses/${id}`, { method: 'DELETE', headers: headers() })
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
    },
    setDbCachedResponseStories: (id: string, storyIds: string[]) =>
      request<DbCachedResponseDetail>(`/admin/cached-responses/${id}/stories`, 'PUT', { storyIds }),
    getCachedResponsesSettings: () =>
      request<{ similarity_threshold: number }>('/admin/cached-responses/settings', 'GET'),
    setCachedResponsesSettings: (similarity_threshold: number) =>
      request<{ similarity_threshold: number }>('/admin/cached-responses/settings', 'PUT', { similarity_threshold }),
    regenDbCachedResponseAudio: (id: string) =>
      request<{ regenerating: string }>(`/admin/cached-responses/${id}/regenerate`, 'POST'),
    deleteDbCachedResponseStoryAudio: async (id: string, character: string, version: number) => {
      const res = await fetch(`/admin/cached-responses/${id}/audio?character=${encodeURIComponent(character)}&version=${version}`, { method: 'DELETE', headers: headers() })
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
    },
    getCharacterAnimConfig: (id: string) =>
      request<CharacterAnimConfig>(`/admin/characters/${id}/anim-config`, 'GET'),
    updateCharacterGestures: (id: string, gestures: string[]) =>
      request<CharacterAnimConfig>(`/admin/characters/${id}/gestures`, 'PUT', { gestures }),
    updateCharacterVocalCues: (id: string, vocalCues: string[]) =>
      request<CharacterAnimConfig>(`/admin/characters/${id}/vocal-cues`, 'PUT', { vocalCues }),
    updateCharacterGestureSynonyms: (id: string, gestureSynonyms: GestureSynonym[]) =>
      request<CharacterAnimConfig>(`/admin/characters/${id}/gesture-synonyms`, 'PUT', { gestureSynonyms }),
    updateCharacterA2FConfig: (id: string, config: Record<string, unknown>) =>
      request<Record<string, unknown>>(`/admin/characters/${id}/a2f-config`, 'PUT', config),
    getAppSettings: () =>
      request<{ defaultStory: string; defaultCharacter: string }>('/admin/app-settings', 'GET'),
    setAppSettings: (settings: { defaultStory: string; defaultCharacter: string }) =>
      request<{ defaultStory: string; defaultCharacter: string }>('/admin/app-settings', 'PUT', settings),
    listAPIKeys: () => request<APIKeyRecord[]>('/admin/api-keys', 'GET'),
    createAPIKey: (name: string, level: 'admin' | 'standard') =>
      request<CreateAPIKeyResult>('/admin/api-keys', 'POST', { name, level }),
    revokeAPIKey: async (id: string) => {
      const res = await fetch(`/admin/api-keys/${id}/revoke`, { method: 'DELETE', headers: headers() })
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
    },
    listGarageObjects: () => request<GarageObject[]>('/admin/garage/objects', 'GET'),
    deleteGarageObject: async (key: string) => {
      const res = await fetch(`/admin/garage/objects?key=${encodeURIComponent(key)}`, { method: 'DELETE', headers: headers() })
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
    },
  }
}

export async function getMe(apiKey: string): Promise<APIKeyRecord> {
  const res = await fetch('/api/me', {
    headers: { 'X-API-Key': apiKey, 'Content-Type': 'application/json' },
  })
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
  return res.json()
}

export async function getMySessions(apiKey: string): Promise<UserSessionBrief[]> {
  const res = await fetch('/api/sessions', {
    headers: { 'X-API-Key': apiKey, 'Content-Type': 'application/json' },
  })
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
  return res.json()
}

export async function validateUserAPIKey(apiKey: string): Promise<boolean> {
  try {
    await getMySessions(apiKey)
    return true
  } catch {
    return false
  }
}
